import UrlPattern from "url-pattern";
import { listRoutes as listDomainRoutes } from "./domain";
import { DOMAIN_USER_MENU } from "@/appConstants";
import { makeNumericSort, objectify } from "@helper/array";
import { whitelistKeys, blacklistKeys } from "@helper/object";
import { containsLink } from "./link";

export function listRoutes(sitemap) {
  if (!sitemap) return [];
  return listDomains(sitemap, { includeUserMenu: true }).reduce(
    (acc, domain) => [...acc, ...listDomainRoutes(domain)],
    [
      {
        key: "RootRedirect",
        type: "redirect",
        path: "/",
        props: {
          to: sitemap.settings?.defaultUrl || sitemap.domains[0].defaultUrl,
        },
      },
    ]
  );
}

export function getRedirectUri(sitemap) {
  if (!sitemap) return null;
  return sitemap.redirectUri;
}

export function getCurrentRole(sitemap) {
  if (!sitemap) return null;
  return sitemap.role;
}

export function getUserMenuDomain(sitemap) {
  return sitemap.domains.find((d) => d.id === DOMAIN_USER_MENU);
}

export function listDomains(sitemap, opts = {}) {
  if (!sitemap) return [];
  const domains = sitemap.domains || [];
  return opts.includeUserMenu
    ? domains
    : domains.filter((d) => d.id !== DOMAIN_USER_MENU);
}

export function isValidImageSource(src) {
  return (
    src &&
    !src.toUpperCase().startsWith("DATABUS://") &&
    !src.toUpperCase().startsWith("FILE://")
  );
}

function addIndex(item, index) {
  return { ...item, index };
}

function navigationSubmap(sitemap) {
  return {
    domains: sitemap.domains
      .map(whitelistKeys(["id", "localKey", "url", "defaultUrl", "groups"]))
      .map((domain) => ({
        ...domain,
        groups: domain.groups
          .map(whitelistKeys(["id", "localKey", "url", "defaultUrl", "links"]))
          .map((group) => ({
            ...group,
            links: group.links.map(
              whitelistKeys(["id", "localKey", "url", "icon"])
            ),
          })),
      })),
  };
}

function buildEditDomainParams(domain, groups) {
  return {
    ...domain,
    groups: Object.values(groups)
      .sort(makeNumericSort((g) => g.index))
      .map(blacklistKeys(["index"]))
      .map((group) => ({
        ...group,
        links: Object.values(group.links)
          .sort(makeNumericSort((g) => g.index))
          .map(blacklistKeys(["index"])),
      })),
  };
}

function buildDefaultGroup(url) {
  const defaultUrl = `${url}/link`;
  return {
    id: "DefaultGroup",
    url,
    defaultUrl,
    links: [
      {
        id: "DefaultLink",
        url: defaultUrl,
      },
    ],
  };
}

export function buildNavigationSaveParams(sitemap, editDomainId, editData) {
  const navigation = navigationSubmap(sitemap);
  return {
    navigation: {
      defaultUrl: sitemap.defaultUrl,
      domains: Object.values(editData.domains)
        .sort(makeNumericSort((d) => d.index))
        .map(blacklistKeys(["index"]))
        .map((domain) =>
          domain.id === editDomainId
            ? buildEditDomainParams(domain, editData.groups)
            : {
                ...domain,
                groups: navigation.domains.find(({ id }) => id === domain.id)
                  ?.groups || [buildDefaultGroup(domain.defaultUrl)],
              }
        ),
    },
  };
}

export function buildNavigationEdit(sitemap, editDomainId) {
  const navigation = navigationSubmap(sitemap);
  const domains = objectify(
    navigation.domains.map(blacklistKeys(["groups"])).map(addIndex)
  );
  const groups = objectify(
    navigation.domains
      .find(({ id }) => id === editDomainId)
      .groups.map(addIndex)
      .map((group) => ({
        ...group,
        links: objectify(group.links.map(addIndex)),
      }))
  );
  return { domains, groups };
}

function flatTopLevelLinks(sitemap) {
  return sitemap.domains.map((d) => d.groups.map((g) => g.links).flat()).flat();
}

export function activeLinkDeleted(sitemap, navigationEdit, linkId, domainId) {
  const deletedFromGroups = !Object.values(navigationEdit.groups)
    .map((g) => Object.values(g.links))
    .flat()
    .map((e) => flatTopLevelLinks(sitemap).find((l) => l.id === e.id))
    .some((l) => l && containsLink(l, linkId));

  const deletedDomain = !Object.values(navigationEdit.domains).some(
    (d) => d.id === domainId
  );

  return deletedFromGroups || deletedDomain;
}

export function resolveBinding(sitemap, key, context) {
  if (!key) return null;
  if (!sitemap.bindings) return null;
  const pattern = sitemap.bindings.find(
    (b) => b.key.replace("\u{00a0}", "") === key.replace("\u{00a0}", "")
  )?.url;
  return pattern ? new UrlPattern(pattern).stringify(context) : null;
}
